import { LightMode } from "@/components/ui/color-mode";
import {
  BackupCheckStatus,
  CashRequestStatus,
  CheckRequestStatus,
  CounterDeliveryStatus,
  FutureContractStatus,
  PurchaseOrderIntentStatus,
  ScheduledTransferStatus,
  SellOrderStatus,
  SuppliesOrderStatus,
  SuppliesRequestStatus,
  SlotRequestStatus,
  OrderStatus,
  TransferRequestStatus,
  SlotStatus,
  LegalOnboardingStatus,
} from "@/constants/entity-status";
import { AppColors } from "@/styles/colors";
import { Badge, Text } from "@chakra-ui/react";
import { useMemo } from "react";

interface Props {
  status?:
    | CashRequestStatus
    | CheckRequestStatus
    | TransferRequestStatus
    | ScheduledTransferStatus
    | SellOrderStatus
    | CounterDeliveryStatus
    | FutureContractStatus
    | SlotRequestStatus
    | SuppliesRequestStatus
    | SuppliesOrderStatus
    | OrderStatus
    | SlotStatus
    | BackupCheckStatus
    | LegalOnboardingStatus
    | PurchaseOrderIntentStatus;
  noPaddingY?: boolean;
  isErrorBadge?: boolean
}

export const RequestStatusBadge = ({ status, noPaddingY , isErrorBadge = false}: Props) => {
  const { bgColor, label } = useMemo(() => {
    const base = (() => {
      switch (status) {
      case CashRequestStatus.AVAILABLE:
      case CheckRequestStatus.AVAILABLE:
      case SlotRequestStatus.SLOTS_PARTIALLY_ASSIGNED:
      case SlotRequestStatus.SLOTS_ASSIGNED:
      case OrderStatus.CONFIRMED:
      case SlotStatus.ASSIGNED:
      case SuppliesRequestStatus.ORDER_CREATED:
        return { bgColor: AppColors.LIGHT_GREEN, label: status };
      case CashRequestStatus.IN_PROCESS:
      case CheckRequestStatus.IN_PROCESS:
      case TransferRequestStatus.PENDING:
      case SellOrderStatus.IN_PROCESS:
      case SlotRequestStatus.PENDING:
      case SuppliesRequestStatus.PENDING:
      case SuppliesRequestStatus.IN_TRANSIT:
      case SuppliesOrderStatus.PENDING:
      case SuppliesOrderStatus.IN_TRANSIT:
      case SlotStatus.PENDING:
      case SlotStatus.TRANSIT:
      case LegalOnboardingStatus.PENDING:
        return { bgColor: AppColors.YELLOW_SECONDARY, label: status };
      case OrderStatus.TRANSIT:
        return { bgColor: AppColors.YELLOW_SECONDARY, label: "En tránsito" };
      case SuppliesRequestStatus.PARTIAL_DELIVERY:
          return { bgColor: AppColors.LIGHT_GREEN, label: "Entrega parcial" };
      case CashRequestStatus.NEEDS_INFO:
      case CheckRequestStatus.NEEDS_INFO:
      case SlotRequestStatus.NEEDS_INFO:
      case SuppliesRequestStatus.NEEDS_INFO:
        return {
          bgColor: AppColors.BLACK_TEXT_SECONDARY,
          label: "Requiere información",
        };
      case SellOrderStatus.IN_COMMUNICATION:
      case CounterDeliveryStatus.IN_COMMUNICATION:
      case FutureContractStatus.IN_COMMUNICATION:
      case PurchaseOrderIntentStatus.IN_COMMUNICATION:
        return {
          bgColor: AppColors.BLACK_TEXT_SECONDARY,
          label: "En comunicación",
        };
      case SellOrderStatus.NEGOTIATION:
      case CounterDeliveryStatus.NEGOTIATION:
      case FutureContractStatus.NEGOTIATION:
        return {
          bgColor: AppColors.BLACK_TEXT_SECONDARY,
          label: "Negociación",
        };
      case LegalOnboardingStatus.IN_REVIEW:
        return { bgColor: AppColors.BLACK_TEXT_SECONDARY, label: status };
      case SuppliesOrderStatus.NOT_DELIVERED:
        return { bgColor: AppColors.RED_ICON, label: status };
      case LegalOnboardingStatus.PRELOADED:
        return { bgColor: AppColors.GRAY_BG, label: status};
      case LegalOnboardingStatus.REQUIRES_MODIF:
        return { bgColor: AppColors.RED_ICON_ALT, label: status };
      case CashRequestStatus.REJECTED:
      case CheckRequestStatus.REJECTED:
      case TransferRequestStatus.REJECTED:
      case TransferRequestStatus.FAILED:
      case ScheduledTransferStatus.REJECTED:
      case ScheduledTransferStatus.FAILED:
      case SellOrderStatus.REJECTED:
      case SuppliesRequestStatus.REJECTED:
      case PurchaseOrderIntentStatus.REJECTED:
        return { bgColor: AppColors.RED_ICON, label: status };
      case CashRequestStatus.FINISHED:
      case CheckRequestStatus.FINISHED:
      case TransferRequestStatus.FINISHED:
      case ScheduledTransferStatus.FINISHED:
      case SellOrderStatus.FINISHED:
      case SellOrderStatus.CONFIRMED:
      case CounterDeliveryStatus.CONFIRMED:
      case FutureContractStatus.CONFIRMED:
      case OrderStatus.DELIVERED:
      case BackupCheckStatus.FINISHED:
      case SuppliesRequestStatus.PARTIALLY_CONFIRMED:
      case SuppliesRequestStatus.ORDER_CONFIRMED:
      case SuppliesRequestStatus.DELIVERED:
      case SuppliesOrderStatus.CONFIRMED:
      case SuppliesOrderStatus.DELIVERED:
      case SlotStatus.DELIVERED:
      case LegalOnboardingStatus.APPROVED:
        return { bgColor: AppColors.GREEN_PRIMARY, label: status };
      case ScheduledTransferStatus.SCHEDULED:
        return { bgColor: AppColors.YELLOW_ALT, label: status };
      case ScheduledTransferStatus.ON_REVISION:
        return {
          bgColor: AppColors.BLACK_TEXT_SECONDARY,
          label: "En revisión",
        };
      case BackupCheckStatus.PENDING_CHECK:
        return {
          bgColor: AppColors.YELLOW_SECONDARY,
          label: "Pendiente de cheque",
        };
      case BackupCheckStatus.PENDING_ENDOSE:
        return {
          bgColor: AppColors.GREEN_SECONDARY,
          label: "Pendiente de endoso",
        };
      case SellOrderStatus.CANCELLED:
      case CounterDeliveryStatus.CANCELLED:
      case FutureContractStatus.CANCELLED:
      case BackupCheckStatus.CANCELLED:
      case SuppliesRequestStatus.CANCELLED:
      case SuppliesOrderStatus.REJECTED:
      case PurchaseOrderIntentStatus.CANCELLED:
        return { bgColor: AppColors.ERROR_TEXT, label: status };

      case PurchaseOrderIntentStatus.PRESENTED:
        return { bgColor: AppColors.GREEN_PRIMARY, label: status };

      case PurchaseOrderIntentStatus.PENDING:
        return { bgColor: AppColors.YELLOW_SECONDARY, label: status };
      default:
        return { bgColor: "transparent", label: status };
      }
    })();

    if (isErrorBadge) {
      return {
        ...base,
        bgColor: AppColors.RED_ICON_ALT,
      };
    }

    return base;
  }, [status, isErrorBadge]);

  if (!status) return null;

  return (
    <LightMode>
      <Badge bg={bgColor}>
        <Text
          paddingY={noPaddingY ? "" : "4px"}
          paddingX="8px"
          fontSize="12px"
          color={AppColors.WHITE_SECONDARY}
          data-testid="request-status-badge-text"
        >
          {label}
        </Text>
      </Badge>
    </LightMode>
  );
};
